import axios from 'axios';
import jwt_decode from 'jwt-decode'
import router from '@/router/index.js';

export const BASE_URL = process.env.VUE_APP_API_BASE_URL;

const apiClientMultipart = axios.create({
  baseURL: BASE_URL,
  processData: false,
  contentType: false,
  cache: false,
  headers: {
    'Content-Type': 'multipart/form-data'
  },
});


const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
  },
});

const tokenRefreshClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── TOKEN REFRESH LOCK ─────────────────────────────────────
// Prevents multiple simultaneous requests from each triggering
// their own refresh call. The first request refreshes; the rest
// queue up and reuse the new token once it arrives.
let isRefreshing = false;
let refreshQueue = [];

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  refreshQueue = [];
}

function isAccessTokenExpired() {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) return true;

  try {
    const decodedToken = jwt_decode(accessToken);
    const currentTime = Date.now() / 1000; // in seconds
    return decodedToken.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // treat as expired if decoding fails
  }
}

// ─── apiClient INTERCEPTOR ──────────────────────────────────
apiClient.interceptors.request.use(async (config) => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  const skipTokenCheckPatterns = [
    '/api/auth',
    '/api/settings/postupdatepass',
  ];

  const shouldSkip = skipTokenCheckPatterns.some(path => config.url.includes(path));

  // If access token is expired, try refreshing
  if (!shouldSkip && isAccessTokenExpired()) {

    // If another request is already refreshing, wait for it
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((newToken) => {
        config.headers.Authorization = `Bearer ${newToken}`;
        return config;
      }).catch((err) => {
        return Promise.reject(err);
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await tokenRefreshClient.post('/api/auth/refresh', { refreshToken });
      const newAccessToken = refreshResponse.data.accessToken;

      // Update the stored access token
      localStorage.setItem('accessToken', newAccessToken);

      // Set the new access token in the request headers
      config.headers.Authorization = `Bearer ${newAccessToken}`;

      // Unblock all queued requests with the new token
      processQueue(null, newAccessToken);

      return config;
    } catch (error) {
      console.error('Error refreshing token:', error);

      // Reject all queued requests
      processQueue(error, null);

      // Clear localStorage since tokens are invalid
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      // Redirect if on a protected route
      if (router.currentRoute.value.meta.requiresAuth) {
        router.push('/');
      }

      // CRITICAL: reject so the calling code gets an error instead of hanging
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }

  return config;
}, (error) => {
  console.log(error);
  return Promise.reject(error);
});


// ─── apiClientMultipart INTERCEPTOR ─────────────────────────
apiClientMultipart.interceptors.request.use(async (config) => {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  // If access token is expired, try refreshing
  if (config.url !== '/api/auth/refresh' && isAccessTokenExpired()) {

    // If another request is already refreshing, wait for it
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((newToken) => {
        config.headers.Authorization = `Bearer ${newToken}`;
        return config;
      }).catch((err) => {
        return Promise.reject(err);
      });
    }

    isRefreshing = true;

    try {
      const refreshResponse = await tokenRefreshClient.post('/api/auth/refresh', { refreshToken });
      const newAccessToken = refreshResponse.data.accessToken;

      // Update the stored access token
      localStorage.setItem('accessToken', newAccessToken);

      // Set the new access token in the request headers
      config.headers.Authorization = `Bearer ${newAccessToken}`;

      // Unblock all queued requests with the new token
      processQueue(null, newAccessToken);

      return config;
    } catch (error) {
      console.error('Error refreshing token:', error);

      // Reject all queued requests
      processQueue(error, null);

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      if (router.currentRoute.value.meta.requiresAuth) {
        router.push('/');
      }

      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }

  return config;
}, (error) => {
  console.log(error);
  return Promise.reject(error);
});


export default {
  // Authenticate user and get JWT token
  async login(username, password) {
    try {
      const response = await apiClient.post('/api/auth/signin', {
        username,
        password,
      });

      return {
        success: true,
        data: response.data,
        headers: response.headers
      };

    } catch (error) {
      let message = 'An error occurred. Please try again.';
      console.log(error.response)

      if (error.response) {
        // Server responded with a status outside 2xx
        if (error.response.status === 401) {
          message = 'Invalid username or password.';
        } else if (error.response.status === 404) {
          message = 'User not found';
        } else if (error.response.status === 502) {
          message = 'Service is temporarily unavailable.';
        } else if (error.response.data) {
          message = error.response.data;
        }
      } else if (error.request) {
        // Request was made but no response
        message = 'No response from server. Check your internet connection.';
      }

      return {
        success: false,
        error: message,
      };
    }
  },


  logout() {
    localStorage.clear();
    router.push('/');
  },

  async getImage(imageName, type) {
    const url = `/api/images/getimage/${imageName}/${type}`;
    return apiClient.get(url, { responseType: 'blob' });
  },

  async fetchindexpagedata() {
    const response = await apiClient.get('/api/index/getindexpagedata');
    return response.data[0];
  },

  async getclasseswithall() {
    const response = await apiClient.get('/api/index/getclsseswithall');
    console.log(response.data)
    return response.data;
  },

  async getactiveclassesandallumni() {
    const response = await apiClient.get('/api/index/getactiveclassesandallumni');
    return response.data;
  },

  async getstreams() {
    const response = await apiClient.get('/api/index/getstreams');
    return response.data;
  },

  async getcounty() {
    const response = await apiClient.get('/api/index/getcounty');
    return response.data;
  },

  async getsubcounty() {
    const response = await apiClient.get('/api/index/getsubcounty');
    return response.data;
  },

  async gethostels() {
    const response = await apiClient.get('/api/index/gethostels');
    return response.data;
  },

  async getAllStudents() {
    const response = await apiClient.get('/api/reg/getActiveStudents');
    return response.data;
  },


  async getpopulationfigures() {
    const response = await apiClient.get('/api/index/getpopulationfigures');
    return response.data[0];
  },

  async getpopulationperclass() {
    const response = await apiClient.get('/api/index/getpopulationperclass');
    return response.data;
  },

  async getUserPrivileges() {
    const response = await apiClient.get('/api/index/getuserpriveleges');
    return response.data;
  },


  async uploadFileWithProgress(file, onUploadProgress, uploadUrl, otherdata) {
    let formData = new FormData();
    formData.append('file', file);  // Append the file
    formData.append('otherdata', otherdata);  // Append examId or any other parameter

    return apiClientMultipart.post(uploadUrl, formData, {
      onUploadProgress,
      headers: {
        'Content-Type': 'multipart/form-data'  // Ensure the correct header is set
      }
    })
  },


  async getDataDynamic(path) {
    const response = await apiClient.get(path);
    return response.data;
  },

  // WhatsApp API Methods
  async getWhatsappMessages(chatId, isGroup) {
    // Placeholder endpoint - User to verify
    const endpoint = `/api/whatsapp/messages/${chatId}`;
    const response = await apiClient.get(endpoint, {
      params: { isGroup }
    });
    return response.data;
  },

  async sendWhatsappMessage(payload) {
    // Placeholder endpoint - User to verify
    const endpoint = `/api/whatsapp/send`;
    const response = await apiClient.post(endpoint, payload);
    return response.data;
  },

  apiClient,
  apiClientMultipart,
  // Other API methods for your application
};
